import React, { useCallback, useEffect, useState } from 'react'
import { Checkbox, Modal, Spin } from 'antd'
import { Icon } from '@iconify/react'

import { showActionNotification } from '../../../components/notification'
import { listTalentPermissions, setTalentPermissions } from '../talent.service'
import pageStyles from '../talent.module.css'
import styles from './TalentPermissionsModal.module.css'

const PERMISSION_MODULES = [
    {
        key: 'support',
        label: 'Support',
        icon: 'si:bar-chart-line',
        iconStyle: { transform: 'rotate(-180deg)' },
        hint: 'Support workspace and tickets',
    },
    // {
    //     key: 'development',
    //     label: 'Development',
    //     icon: 'mdi:code-tags',
    //     hint: 'Sprints, tasks, and dev board',
    // },
    {
        key: 'storymap',
        label: 'Story Map',
        icon: 'mdi:map-marker-path',
        hint: 'Journeys, stories, and releases',
    },
    {
        key: 'test',
        label: 'Test',
        icon: 'streamline:test-tube',
        hint: 'Test cases and QA workspace',
    },
    {
        key: 'prototypes',
        label: 'Prototype',
        icon: 'mdi:vector-square',
        hint: 'Prototypes and design workspace',
    },
    {
        key: 'reports',
        label: 'Report',
        icon: 'mdi:chart-box',
        hint: 'Report workspace',
    },
    {
        key: 'talent',
        label: 'TalentX',
        icon: 'mdi:account-group',
        hint: 'Talent management',
    },
    {
        key: 'clients',
        label: 'Clients',
        icon: 'mdi:account-group-outline',
        hint: 'Client management',
    },
    {
        key: 'marketx',
        label: 'MarketX',
        icon: 'mdi:lightbulb-on-outline',
        hint: 'MarketX and inspiration gallery',
    },
]

const EMPTY_PERMISSIONS = PERMISSION_MODULES.reduce((acc, { key }) => {
    acc[key] = { isRead: false, isEdit: false, isDelete: false }
    return acc
}, {})

const getAccessMeta = (modulePerms) => {
    if (modulePerms?.isEdit && modulePerms?.isDelete) {
        return { label: 'Full + delete', className: styles.accessDelete }
    }
    if (modulePerms?.isEdit) {
        return { label: 'Full access', className: styles.accessFull }
    }
    if (modulePerms?.isRead) {
        return { label: 'View only', className: styles.accessRead }
    }
    return { label: 'No access', className: styles.accessNone }
}

const normalizePermissions = (raw) => {
    const next = { ...EMPTY_PERMISSIONS }
    PERMISSION_MODULES.forEach(({ key }) => {
        const modulePerms = raw?.[key]
        if (!modulePerms) return
        next[key] = {
            isRead: Boolean(modulePerms.isRead),
            isEdit: Boolean(modulePerms.isEdit),
            isDelete: Boolean(modulePerms.isDelete),
        }
    })
    return next
}

const extractPermissionsFromResponse = (data, talentSupportUserId) => {
    const result = data?.result ?? data?.data
    if (!result) return null

    if (Array.isArray(result)) {
        if (!result.length) return null

        const match = talentSupportUserId
            ? result.find((item) => {
                  const itemUserId =
                      item?.talentSupportUserId?._id ||
                      item?.talentSupportUserId
                  return (
                      itemUserId &&
                      String(itemUserId) === String(talentSupportUserId)
                  )
              })
            : null

        const record = match || result[0]
        return record?.permissions ?? null
    }

    if (result?.permissions && typeof result.permissions === 'object') {
        return result.permissions
    }

    if (
        typeof result === 'object' &&
        (result.support ||
            result.development ||
            result.storymap ||
            result.test ||
            result.prototypes ||
            result.reports ||
            result.talent ||
            result.clients)
    ) {
        return result
    }

    return null
}

const getDisplayName = (user) =>
    user?.userName ||
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    'Talent user'

const TalentPermissionsDrawer = ({ open, user, onClose }) => {
    const talentSupportUserId = user?._id || user?.id
    const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const loadPermissions = useCallback(async () => {
        if (!talentSupportUserId) return

        setLoading(true)
        try {
            const { data } = await listTalentPermissions(talentSupportUserId)
            const nextPermissions = extractPermissionsFromResponse(
                data,
                talentSupportUserId
            )
            setPermissions(
                nextPermissions
                    ? normalizePermissions(nextPermissions)
                    : { ...EMPTY_PERMISSIONS }
            )
        } catch {
            setPermissions({ ...EMPTY_PERMISSIONS })
        } finally {
            setLoading(false)
        }
    }, [talentSupportUserId])

    useEffect(() => {
        if (!open || !talentSupportUserId) return
        loadPermissions()
    }, [open, talentSupportUserId, loadPermissions])

    const handleReadChange = (moduleKey, checked) => {
        setPermissions((prev) => ({
            ...prev,
            [moduleKey]: {
                ...prev[moduleKey],
                isRead: checked,
                isEdit: checked ? prev[moduleKey]?.isEdit : false,
                isDelete: checked ? prev[moduleKey]?.isDelete : false,
            },
        }))
    }

    const handleEditChange = (moduleKey, checked) => {
        setPermissions((prev) => ({
            ...prev,
            [moduleKey]: {
                isRead: checked ? true : prev[moduleKey]?.isRead,
                isEdit: checked,
                isDelete: checked ? prev[moduleKey]?.isDelete : false,
            },
        }))
    }

    const handleDeleteChange = (moduleKey, checked) => {
        setPermissions((prev) => ({
            ...prev,
            [moduleKey]: {
                isRead: checked ? true : prev[moduleKey]?.isRead,
                isEdit: checked ? true : prev[moduleKey]?.isEdit,
                isDelete: checked,
            },
        }))
    }

    const handleSave = async () => {
        if (!talentSupportUserId) return

        setSaving(true)
        try {
            const { data } = await setTalentPermissions({
                talentSupportUserId,
                permissions,
            })

            if (data?.success) {
                showActionNotification({
                    type: 'success',
                    message: 'Permissions saved successfully',
                })
                await loadPermissions()
                onClose?.()
                return
            }

            showActionNotification({
                type: 'error',
                message: data?.message || 'Failed to save permissions',
            })
        } catch {
            showActionNotification({
                type: 'error',
                message: 'Failed to save permissions',
            })
        } finally {
            setSaving(false)
        }
    }

    const displayName = getDisplayName(user)

    return (
        <Modal
            title={
                <div className={styles.modalTitleWrap}>
                    <span className={styles.modalTitle}>Permissions</span>
                    <span className={styles.modalSubtitle}>
                        {displayName}
                        {user?.email ? ` · ${user.email}` : ''}
                    </span>
                </div>
            }
            open={open}
            onCancel={onClose}
            centered
            width={580}
            destroyOnClose
            className={pageStyles.permissionsModal}
            footer={
                <div className={styles.modalFooter}>
                    <div className={styles.legend}>
                        <span className={styles.legendItem}>
                            <span
                                className={`${styles.legendDot} ${styles.legendDotRead}`}
                                aria-hidden
                            />
                            Read
                        </span>
                        <span className={styles.legendItem}>
                            <span
                                className={`${styles.legendDot} ${styles.legendDotEdit}`}
                                aria-hidden
                            />
                            Edit
                        </span>
                        <span className={styles.legendItem}>
                            <span
                                className={`${styles.legendDot} ${styles.legendDotDelete}`}
                                aria-hidden
                            />
                            Delete
                        </span>
                    </div>
                    <div className={styles.formActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || loading || !talentSupportUserId}
                            className={`${styles.submitButton} ${
                                saving || loading
                                    ? styles.submitButtonDisabled
                                    : ''
                            }`}
                        >
                            {saving ? (
                                <>
                                    <Icon
                                        icon="line-md:loading-loop"
                                        width={16}
                                        height={16}
                                        className={styles.submitSpinner}
                                    />
                                    Saving...
                                </>
                            ) : (
                                'Save permissions'
                            )}
                        </button>
                    </div>
                </div>
            }
        >
            <Spin spinning={loading}>
                <div className={styles.permissionsBody}>
                    <div className={styles.introCard}>
                        <div className={styles.introIconWrap} aria-hidden>
                            <Icon
                                icon="mdi:shield-account-outline"
                                width={18}
                                height={18}
                            />
                        </div>
                        <div className={styles.introText}>
                            <p className={styles.introDesc}>
                                Decide what this person can see and change in
                                each module.
                            </p>
                            <p className={styles.introNote}>
                                Edit also allows viewing. Delete can be enabled
                                only when Edit is on.
                            </p>
                        </div>
                    </div>

                    <div className={styles.permissionsPanel}>
                        <div className={styles.permissionsHeader}>
                            <span>Module</span>
                            <span>Read</span>
                            <span>Edit</span>
                            <span>Delete</span>
                        </div>
                        <div className={styles.permissionsList}>
                            {PERMISSION_MODULES.map(
                                ({ key, label, icon, iconStyle, hint }) => {
                                    const modulePerms = permissions[key]
                                    const accessMeta =
                                        getAccessMeta(modulePerms)

                                    return (
                                        <div
                                            key={key}
                                            className={styles.permissionsRow}
                                        >
                                            <div className={styles.moduleCell}>
                                                <span
                                                    className={
                                                        styles.moduleIconWrap
                                                    }
                                                    aria-hidden
                                                >
                                                    <Icon
                                                        icon={icon}
                                                        width={14}
                                                        height={14}
                                                        style={iconStyle}
                                                    />
                                                </span>
                                                <div
                                                    className={
                                                        styles.moduleText
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.moduleLabelRow
                                                        }
                                                    >
                                                        <span
                                                            className={
                                                                styles.moduleLabel
                                                            }
                                                        >
                                                            {label}
                                                        </span>
                                                        <span
                                                            className={`${styles.accessBadge} ${accessMeta.className}`}
                                                        >
                                                            {accessMeta.label}
                                                        </span>
                                                    </div>
                                                    <span
                                                        className={
                                                            styles.moduleHint
                                                        }
                                                    >
                                                        {hint}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className={styles.checkCell}>
                                                <Checkbox
                                                    checked={
                                                        modulePerms?.isRead
                                                    }
                                                    onChange={(e) =>
                                                        handleReadChange(
                                                            key,
                                                            e.target.checked
                                                        )
                                                    }
                                                    aria-label={`${label} read access`}
                                                />
                                            </div>
                                            <div className={styles.checkCell}>
                                                <Checkbox
                                                    checked={
                                                        modulePerms?.isEdit
                                                    }
                                                    onChange={(e) =>
                                                        handleEditChange(
                                                            key,
                                                            e.target.checked
                                                        )
                                                    }
                                                    aria-label={`${label} edit access`}
                                                />
                                            </div>
                                            <div className={styles.checkCell}>
                                                <Checkbox
                                                    checked={
                                                        modulePerms?.isDelete
                                                    }
                                                    onChange={(e) =>
                                                        handleDeleteChange(
                                                            key,
                                                            e.target.checked
                                                        )
                                                    }
                                                    aria-label={`${label} delete access`}
                                                />
                                            </div>
                                        </div>
                                    )
                                }
                            )}
                        </div>
                    </div>
                </div>
            </Spin>
        </Modal>
    )
}

export default TalentPermissionsDrawer
